/**
 * §10 — timed resources. The meter is recomputed in the browser between polls
 * using the very functions the API bills with (`@/lib/domain`), so the running
 * total on screen and the charge that eventually posts cannot disagree.
 */
import { useEffect, useState } from 'react';
import { billableSeconds, priceForDuration, selectRule } from '@/lib/domain';
import type { ResourceSessionStatus, ResourceStatus, ResourceType } from '@/lib/shared-types';
import type { PricingListRef, PricingRuleRow } from '@/Pages/Pricing/pricingListsApi';

/**
 * The rule shape moved to `Pages/Pricing` with the rest of the pricing wire
 * contract, and is re-exported here because half the floor screens import it
 * from this file. A type-only import, so nothing about the meter now depends on
 * the cache layer at runtime.
 */
export type { PricingRuleRow };

/**
 * The rate card a resource charges from, with its rules when the payload
 * carried them.
 *
 * This replaced a `pricingRules` array hanging off the resource itself, which is
 * the whole change: ten PlayStations used to carry ten copies of the same four
 * numbers, and a meter reading one of them could not tell whether the other nine
 * agreed. `GET /resources` embeds the three summary fields; `GET /resources/:id`
 * and `GET /resource-sessions` embed the rules as well.
 */
export interface AttachedPricingList extends PricingListRef {
  rules?: PricingRuleRow[];
}

export interface ResourceRow {
  /** I-13 — the resource's own branch currency, resolved server-side. */
  currency?: string;
  id: string;
  areaId: string;
  type: ResourceType;
  name: string;
  code: string | null;
  capacity: number;
  status: ResourceStatus;
  isActive: boolean;
  area?: { id: string; name: string; floorId: string } | null;
  /** The raw column, which is what the resource form writes back. */
  pricingListId?: string | null;
  pricingList?: AttachedPricingList | null;
}

/** §6.3 / P10-08 — what a package entitlement is paying for on this session. */
export interface SessionEntitlement {
  entitlementId: string;
  packageName: string | null;
  /** Minutes drawn off the grant and held for this session. */
  heldMinutes: number;
  /** Minutes of play the grant pays for. */
  coveredMinutes: number;
  coveredSeconds: number;
  /** What is left on the grant once this session settles. */
  remainingMinutes: number;
  expiresAt: string | null;
}

export interface SessionBilling {
  ruleId: string | null;
  ruleName?: string;
  /** Total seconds played, covered or not. */
  playedSeconds?: number;
  playedMinutes?: number;
  /** Seconds the guest actually pays for — play minus entitlement coverage. */
  billableSeconds: number;
  billableMinutes: number;
  units?: number;
  unitPrice?: string;
  amount: string | null;
  remainingSeconds?: number;
  entitlement?: SessionEntitlement | null;
}

export interface SessionRow {
  /** I-13 — the meter's currency, resolved server-side. */
  currency?: string;
  id: string;
  resourceId: string;
  /**
   * §11 — the table this meter is run as part of, and the child in the room.
   *
   * Both replaced `partyId`/`participantId`. The table is how the TIME charge
   * reaches a bill (through that table's open visit); the child is an id and
   * nothing else, because `GET /resource-sessions` deliberately does not carry a
   * child's name, band or guardians — those sit behind `children.view`, which
   * the floor roles do not hold. The kids desk loads them itself.
   */
  tableId?: string | null;
  childId?: string | null;
  /** The bill this meter was opened against, when it was opened against one. */
  checkId?: string | null;
  status: ResourceSessionStatus;
  playerCount: number;
  startedAt: string | null;
  pausedAt: string | null;
  endedAt: string | null;
  accumulatedSeconds: number;
  plannedMinutes: number | null;
  autoCloseAt: string | null;
  table?: { id: string; number: string; areaId?: string } | null;
  entitlementId?: string | null;
  pricingRule?: PricingRuleRow | null;
  resource?: { id: string; name: string; pricingList?: AttachedPricingList | null } | null;
  billing?: SessionBilling;
}

export interface Meter {
  /** Seconds played. */
  seconds: number;
  /** Seconds left of the planned window / auto-close, when there is one. */
  remaining: number | null;
  /** Seconds of the play a package entitlement is paying for (P10-08). */
  coveredSeconds: number;
  /** Seconds the guest is charged for — `seconds` minus `coveredSeconds`. */
  billableSeconds: number;
  amount: number | null;
  ruleName: string | null;
  /** The grant in play, when there is one. */
  entitlement: SessionEntitlement | null;
  running: boolean;
}

/** Nearly out of time — matches the API's 10/5-minute expiry warnings. */
export const EXPIRING_SECONDS = 5 * 60;

function ruleFor(session: SessionRow, at: Date): PricingRuleRow | null {
  if (session.pricingRule) return session.pricingRule;
  // The candidates are the rate card's, not the resource's: a resource attached
  // to nothing has none, which is a different state from having an empty card
  // and is why the API refuses the start with two different codes.
  const rules = session.resource?.pricingList?.rules ?? [];
  const pinned = session.billing?.ruleId
    ? rules.find((rule) => rule.id === session.billing?.ruleId)
    : undefined;
  return pinned ?? selectRule(rules.map(toDomainRule), at)?.source ?? null;
}

/** `selectRule` speaks the domain shape; keep the original row alongside it. */
function toDomainRule(rule: PricingRuleRow) {
  return {
    unit: rule.unit,
    price: Number(rule.price),
    durationMinutes: rule.durationMinutes,
    minMinutes: rule.minMinutes,
    daysOfWeek: rule.daysOfWeek,
    startTime: rule.startTime,
    endTime: rule.endTime,
    priority: rule.priority,
    isActive: rule.isActive,
    source: rule,
  };
}

export function meterFor(session: SessionRow, now: Date): Meter {
  const seconds = billableSeconds(session, now);
  const rule = ruleFor(session, now);

  // P10-08 — minutes already paid for by a package are not billed again. The
  // API settles exactly this way at stop, so the meter on screen and the charge
  // that eventually posts still cannot disagree; the grant simply covers the
  // first `coveredSeconds` of the session and only the rest is priced.
  const entitlement = session.billing?.entitlement ?? null;
  const heldSeconds = (entitlement?.heldMinutes ?? 0) * 60;
  const coveredSeconds = Math.min(seconds, heldSeconds);
  const chargeable = Math.max(0, seconds - coveredSeconds);

  const remaining = session.autoCloseAt
    ? Math.max(0, Math.round((new Date(session.autoCloseAt).getTime() - now.getTime()) / 1000))
    : session.plannedMinutes
      ? Math.max(0, session.plannedMinutes * 60 - seconds)
      : null;

  const amount =
    chargeable === 0 && coveredSeconds > 0
      ? 0
      : rule
        ? priceForDuration(
            {
              unit: rule.unit,
              price: Number(rule.price),
              durationMinutes: rule.durationMinutes,
              minMinutes: rule.minMinutes,
            },
            chargeable,
          )
        : session.billing?.amount
          ? Number(session.billing.amount)
          : null;

  return {
    seconds,
    remaining,
    coveredSeconds,
    billableSeconds: chargeable,
    amount,
    ruleName: rule?.name ?? session.billing?.ruleName ?? null,
    entitlement,
    running: session.status === 'ACTIVE',
  };
}


/**
 * The clock the meters are drawn against.
 *
 * §10 — the countdown and the running total are recomputed locally each second
 * from `@/lib/domain`; the server is only re-read when an event says something
 * actually changed. This is that second, and nothing more: it is deliberately
 * *not* a poll, and it stops entirely when nothing is running or when whatever
 * shows a meter is closed — the floor map itself must not repaint 60 times a
 * minute for a panel nobody has open.
 */
export function useMeterTick(active: boolean): Date {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    if (!active) return;
    // Catch up first: a panel opened after a quiet minute would otherwise draw
    // one stale frame before the first interval fires.
    setNow(new Date());
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, [active]);

  return now;
}
